/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useState, useEffect, useCallback } from 'react';

import { useHistory, useParams } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';

import {
    Flex,
    View,
    Item,
    TabList,
    TabPanels,
    Tabs,
    IllustratedMessage,
    Heading,
    Content,
    ProgressCircle,
    Provider,
    lightTheme
} from '@adobe/react-spectrum';
import Alert from '@spectrum-icons/workflow/Alert';
import DOMPurify from 'dompurify';

import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';
import { trackPage, trackEvent } from '../../utils/tracking';
import { event_productproperties_close } from '../../utils/trackingEvents';
import { product_properties_page } from '../../utils/trackingPages';
import { productWithVariantProperties } from '../../queries/2.3.5';
import { formatPrices } from '../../utils/priceFormatting';
import ImageGallery from './ImageGallery';
import VariantTable from './VariantTable';
import { typeNameToText } from './utils';
import ContentReferenceTab from './ContentReferenceTab';
import PropertiesHeader from './PropertiesHeader';

import classes from './Properties.scss';

const messages = defineMessages({
    errorInvalidConfig: {
        id: 'error.invalidConfig',
        defaultMessage: 'Invalid Configuration.'
    },
    errorProductNotFound: {
        id: 'error.productNotFound',
        defaultMessage: 'Product not found.'
    },
    errorProductNotAvailable: {
        id: 'error.productNotAvailable',
        defaultMessage: 'Product not available.'
    },
    headlineVariants: {
        id: 'headline.variants',
        defaultMessage: 'Variants'
    },
    labelProductPropertiesTabs: {
        id: 'label.productPropertiesTabs',
        defaultMessage: 'Product Properties Tabs'
    },
    labelLoading: {
        id: 'label.loading',
        defaultMessage: 'Loading…'
    },
    labelName: {
        id: 'label.name',
        defaultMessage: 'Name'
    },
    labelType: {
        id: 'label.type',
        defaultMessage: 'Type'
    },
    labelSku: {
        id: 'label.sku',
        defaultMessage: 'SKU'
    },
    labelUrlKey: {
        id: 'label.urlKey',
        defaultMessage: 'URL Key'
    },
    labelRegularPrice: {
        id: 'label.regularPrice',
        defaultMessage: 'Regular Price'
    },
    labelDiscount: {
        id: 'label.discount',
        defaultMessage: 'Discount'
    },
    labelFinalPrice: {
        id: 'label.finalPrice',
        defaultMessage: 'Final Price'
    },
    labelDescription: {
        id: 'label.description',
        defaultMessage: 'Description'
    },
    headlineProductProperties: {
        id: 'headline.productProperties',
        defaultMessage: 'Product Properties'
    },
    headlineGeneral: {
        id: 'headline.general',
        defaultMessage: 'General'
    },
    tabDetails: {
        id: 'tab.details',
        defaultMessage: 'Details'
    },
    tabContentReferences: {
        id: 'tab.contentReferences',
        defaultMessage: 'Content References'
    }
});

/**
 * Displays a product properties page with main product attributes, variants, product images and content references.
 */
const ProductProperties = () => {
    const history = useHistory();
    const { configId, productSku } = useParams();
    const [schemaStatus, , configs] = useGraphqlSchemaContext();
    const [product, setProduct] = useState();
    const [error, setError] = useState();
    const { formatMessage, formatNumber } = useIntl();

    const loadProduct = useCallback(async () => {
        if (schemaStatus !== 'done') {
            return;
        }

        const config = configs.find(c => c.identifier === configId);

        if (schemaStatus === 'error' || !config) {
            setError(formatMessage(messages.errorInvalidConfig));
            return;
        }

        try {
            const result = await config.gqlClient.query({
                query: productWithVariantProperties,
                variables: { sku: productSku }
            });

            if (
                result &&
                result.data &&
                result.data.products &&
                result.data.products.items &&
                result.data.products.items.length > 0
            ) {
                setProduct(result.data.products.items[0]);
            } else {
                setError(formatMessage(messages.errorProductNotFound));
            }
        } catch (err) {
            console.error(err);
            setError(err.toString());
        }
    }, [schemaStatus, configs]);

    useEffect(() => loadProduct(), [loadProduct]);

    useEffect(() => {
        trackPage({ ...product_properties_page, assetId: productSku });
    }, []);

    const onGoBack = () => {
        trackEvent(event_productproperties_close);
        if (history.length == 1) {
            window.close();
        } else {
            history.goBack();
        }
    };

    if (error) {
        return (
            <div className={classes.root}>
                <View height="100%">
                    <PropertiesHeader
                        onGoBack={onGoBack}
                        title={formatMessage(messages.headlineProductProperties)}
                        subtitle={formatMessage(messages.errorProductNotAvailable)}
                    />
                    <View height="calc(100% - size-675 - size-800)" padding="size-400" overflow="auto">
                        <IllustratedMessage>
                            <Alert size="XXL" />
                            <Heading>{formatMessage(messages.errorProductNotAvailable)}</Heading>
                            <Content>{error}</Content>
                        </IllustratedMessage>
                    </View>
                </View>
            </div>
        );
    }

    let variantsSection = null;
    if (!product || (product && product.__typename === 'ConfigurableProduct' && product.variants.length > 0)) {
        variantsSection = (
            <>
                <View
                    borderWidth="thin"
                    borderColor="mid"
                    borderRadius="medium"
                    backgroundColor="gray-50"
                    marginBottom="size-200"
                >
                    <Provider theme={lightTheme} UNSAFE_className={classes.provider}>
                        <h3 className={classes.widgetHeadline}>{formatMessage(messages.headlineVariants)}</h3>
                        <VariantTable items={product && product.variants} loading={!product} />
                    </Provider>
                </View>
            </>
        );
    }

    let generalSection;
    if (!product) {
        generalSection = (
            <View padding="size-500">
                <IllustratedMessage>
                    <ProgressCircle aria-label={formatMessage(messages.labelLoading)} isIndeterminate />
                </IllustratedMessage>
            </View>
        );
    } else {
        const { regular_price, final_price, isDiscounted, discount_amount } = formatPrices(
            product.price_range,
            formatNumber
        );

        generalSection = (
            <div className={classes.detailTable} role="table">
                <div role="row">
                    <div role="cell">{formatMessage(messages.labelName)}</div>
                    <div role="cell">
                        <strong>{product.name}</strong>
                    </div>
                </div>
                <div role="row">
                    <div role="cell">{formatMessage(messages.labelType)}</div>
                    <div role="cell">{typeNameToText(product.__typename)}</div>
                </div>
                <div role="row">
                    <div role="cell">{formatMessage(messages.labelSku)}</div>
                    <div role="cell">{product.sku}</div>
                </div>
                <div role="row">
                    <div role="cell">{formatMessage(messages.labelUrlKey)}</div>
                    <div role="cell">{product.url_key}</div>
                </div>
                <div role="row">
                    <div role="cell">{formatMessage(messages.labelRegularPrice)}</div>
                    <div role="cell">{regular_price}</div>
                </div>
                {isDiscounted && (
                    <>
                        <div role="row">
                            <div role="cell">{formatMessage(messages.labelDiscount)}</div>
                            <div role="cell">{discount_amount}</div>
                        </div>
                        <div role="row">
                            <div role="cell">{formatMessage(messages.labelFinalPrice)}</div>
                            <div role="cell">{final_price}</div>
                        </div>
                    </>
                )}
                <div role="row">
                    <div role="cell" className={classes.top}>
                        {formatMessage(messages.labelDescription)}
                    </div>
                    <div
                        role="cell"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description.html) }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <Flex direction="column" height="100%">
                <View height="size-1000">
                    <PropertiesHeader
                        onGoBack={onGoBack}
                        title={product ? product.name : formatMessage(messages.headlineProductProperties)}
                        subtitle={product ? typeNameToText(product.__typename) : formatMessage(messages.labelLoading)}
                    />
                </View>
                <View flex overflow="auto" paddingTop="size-400" paddingX="size-400">
                    <Flex direction={{ base: 'column', M: 'row' }} gap="size-400" height="100%">
                        <View width={{ base: '100%', M: '33%' }} minWidth="size-3600">
                            <View
                                borderWidth="thin"
                                borderColor="mid"
                                borderRadius="medium"
                                backgroundColor="gray-50"
                                overflow="hidden"
                            >
                                <ImageGallery loading={!product} images={product && product.media_gallery} />
                            </View>
                        </View>
                        <View flex>
                            <Tabs
                                aria-label={formatMessage(messages.labelProductPropertiesTabs)}
                                isDisabled={!product}
                                height="100%"
                            >
                                <TabList>
                                    <Item key="Details">{formatMessage(messages.tabDetails)}</Item>
                                    <Item key="ContentReferences">{formatMessage(messages.tabContentReferences)}</Item>
                                </TabList>
                                <TabPanels UNSAFE_className={classes.tabPanel}>
                                    <Item key="Details">
                                        <div className={classes.scrollContainer}>
                                            {/* UX Placeholder
                                            <Flex>
                                                <View flex="0 0 auto" paddingY="size-200">
                                                    <ActionButton>
                                                        <EditIn />
                                                        <Text>Edit in Commerce</Text>
                                                    </ActionButton>
                                                </View>
                                            </Flex>  */}
                                            <View
                                                marginY="size-200"
                                                borderWidth="thin"
                                                borderColor="mid"
                                                borderRadius="medium"
                                                backgroundColor="gray-50"
                                            >
                                                <h3 className={classes.widgetHeadline}>
                                                    {formatMessage(messages.headlineGeneral)}
                                                </h3>
                                                {generalSection}
                                            </View>
                                            {variantsSection}
                                        </div>
                                    </Item>
                                    <Item key="ContentReferences">
                                        <div className={classes.scrollContainer}>
                                            <View
                                                marginY="size-200"
                                                borderWidth="thin"
                                                borderColor="mid"
                                                borderRadius="medium"
                                                backgroundColor="gray-50"
                                            >
                                                <ContentReferenceTab type="product" identifier={productSku} />
                                            </View>
                                        </div>
                                    </Item>
                                </TabPanels>
                            </Tabs>
                        </View>
                    </Flex>
                </View>
            </Flex>
        </div>
    );
};

export default ProductProperties;
